import { validateCalculatedField, validateExpression } from "../../components/Survey/Util/calculatedFields";
import { canBeInHorizontalLayout } from "../../components/Survey/Util/formLayout";
import { getFinishStep } from '../../helpers/surveyFinishSteps';
import { isAfter } from 'date-fns';
import moment from 'moment-timezone';
import { getResultIdentifiersWithProvenance } from "../../helpers/survey";
import {
	dateFromDateObjectOrString,
	dateIsValid,
	dateTimeFromDateObjectOrString,
	relativeDateIsValid
} from "../../helpers/dateUtility";

var templateValidatorFactory = function () {

	function validate(surveyObject, template, customFields) {
		if (typeValidators[surveyObject.type]) {
			var validationErrors = [];
			for (var i = 0; i < typeValidators[surveyObject.type].length; i++) {
				validationErrors = validationErrors.concat(typeValidators[surveyObject.type][i](surveyObject, template, customFields));
			}
			return validationErrors;
		}
		return ["Unknown / invalid type"];
	}

	function prefix(array, prefix) {
		for (var i = 0; i < array.length; i++) {
			array[i] = prefix + " > " + array[i];
		}
		return array;
	}

	var typeValidators = {};

	var surveyValidator = function (survey, template, customFields) {
		var validationErrors = [];
		if (!survey.steps) {
			validationErrors.push("At least one step is required.");
		} else {
			var stepIdentifiers = {};
			for (var i = 0; i < survey.steps.length; i++) {
				if (stepIdentifiers[survey.steps[i].identifier]) {
					validationErrors.push("More than one step defined with identifier  \"" + survey.steps[i].identifier + "\"");
				}
				stepIdentifiers[survey.steps[i].identifier] = true;
				validationErrors = validationErrors.concat(prefix(validate(survey.steps[i], template, customFields), survey.steps[i].identifier));

				if (survey.steps[i].navigationRule) {
					validationErrors = validationErrors.concat(prefix(validate(survey.steps[i].navigationRule, template), survey.steps[i].identifier + " > Navigation"));
				}

				if (survey.steps[i].skipNavigationRule) {
					validationErrors = validationErrors.concat(prefix(validate(survey.steps[i].skipNavigationRule, template), survey.steps[i].identifier + " > Navigation"));
				}

				if (survey.steps[i].validationRules && survey.steps[i].validationRules.length) {
					for (var j = 0; j < survey.steps[i].validationRules.length; j++) {
						validationErrors = validationErrors.concat(prefix(validate(survey.steps[i].validationRules[j], template), survey.steps[i].identifier + " > Validation Rule " + j));
					}
				}
			}

			if (survey.webViewStepTemplates) {
				for (var i = 0; i < survey.webViewStepTemplates.length; i++) {
					validationErrors = validationErrors.concat(prefix(validate(survey.webViewStepTemplates[i], template), "Web View Step Templates > " + survey.webViewStepTemplates[i].identifier));
				}
			}

			if (survey.calculatedFields) {
				for (var i = 0; i < survey.calculatedFields.length; i++) {
					validationErrors = validationErrors.concat(prefix(validate(survey.calculatedFields[i], template), "Calculated Fields > " + survey.calculatedFields[i].identifier));
				}
			}

			if (survey.isEma) {
				if (survey.steps.length !== 1 || survey.steps[0].type !== "QuestionStep") {
					validationErrors = validationErrors.concat("An EMA survey can only have a single question step.");
				}
				else {
					if (survey.steps[0].answerFormat.type !== "TextChoiceAnswerFormat" || survey.steps[0].answerFormat.textChoices.length < 2) {
						validationErrors = validationErrors.concat("An EMA survey question needs to have a text choice answer format with at least 2 choices.");
					}
					if (!survey.steps[0].text) {
						validationErrors = validationErrors.concat("Question text is required for an EMA survey.");
					}
				}
			}

			if (survey.requireUniqueIdentifiers) {
				const resultIdentifiers = getResultIdentifiersWithProvenance(survey);
				resultIdentifiers.forEach(({ identifier, sources }) => {
					// If there is only one source, there is no duplication.
					if (sources.length === 1) return;
					// Duplicate steps are already handled elsewhere.
					if (sources.every(source => source.type === 'Step')) return;
					// Duplicate form items within the same step are already handled elsewhere.
					if (sources.every(source => source.type === 'FormItem' && source.parentIdentifier === sources[0].parentIdentifier)) return;
					// Duplicate calculated fields are already handled elsewhere.
					if (sources.every(source => source.type === 'CalculatedField')) return;

					const uniqueSourceNames = Array.from(new Set(sources.map(source => source.name)));
					validationErrors.push(`Duplicate result identifier "${identifier}" (${uniqueSourceNames.join(', ')}).`);
				});
			}
		}

		return validationErrors;
	};
	typeValidators["OrderedTask"] = [surveyValidator];
	typeValidators["NavigableOrderedTask"] = [surveyValidator];
	typeValidators["PrefilledOrderedTask"] = [surveyValidator];
	typeValidators["PrefilledNavigableOrderedTask"] = [surveyValidator];
	typeValidators["RKStudioOrderedTask"] = [surveyValidator];
	typeValidators["RKStudioNavigableOrderedTask"] = [surveyValidator];

	var baseStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.identifier) {
			validationErrors.push("Step must have an identifier");
		}
		if (getFinishStep(step.identifier)) {
			validationErrors.push("Is a reserved word and not allowed as a step identifier");
		}
		return validationErrors;
	};

	var questionStepValidator = function (step, template, customFields) {
		var validationErrors = [];
		if (!step.answerFormat) {
			validationErrors.push("Question Step must have an Answer Format");
		} else {
			validationErrors = validationErrors.concat(prefix(validate(step.answerFormat, template, customFields), "Answer Format"));
		}
		return validationErrors;
	};

	var endOfSurveyStepValidator = function (step, template) {
		return validate(step.renderStep, template);
	}

	var formStepValidator = function (step, template, customFields, formItemsOptional) {
		var validationErrors = [];
		if (!step.formItems && !formItemsOptional) {
			validationErrors.push("Form Step must have form items");
		} else if (step.formItems?.length) {
			var formItemIdentifiers = {};
			for (var i = 0; i < step.formItems.length; i++) {
				if (!step.formItems[i].identifier) {
					validationErrors.push(`Form item must have an identifier`);
				}
				if (formItemIdentifiers[step.formItems[i].identifier]) {
					validationErrors.push("More than one form item defined with identifier \"" + step.formItems[i].identifier + "\"");
				}
				if (canBeInHorizontalLayout(step.formItems[i]) && step.formItems[i].allowHorizontalLayout && !step.formItems[i].minimumWidth) {
					validationErrors.push(`${step.formItems[i].identifier}: Minimum width must be specified when horizontal layout is enabled`);
				}
				if (step.formItems[i].minimumWidth !== undefined && (step.formItems[i].minimumWidth < 50 || step.formItems[i].minimumWidth > 680)) {
					validationErrors.push(`${step.formItems[i].identifier}: Minimum width must be between 50 and 680`);
				}
				formItemIdentifiers[step.formItems[i].identifier] = true;
				validationErrors = validationErrors.concat(prefix(validate(step.formItems[i], template, customFields), step.formItems[i].identifier));
			}
		}
		return validationErrors;
	};

	const instructionStepValidator = (step, template, customFields) => formStepValidator(step, template, customFields, true);

	var visualConsentStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.consentDocumentTitle) {
			validationErrors.push("Step must reference a valid consent document");
		} else {
			var consentDocument;
			for (var i = 0; i < template.consentDocuments.length; i++) {
				if (template.consentDocuments[i].title == step.consentDocumentTitle) {
					consentDocument = template.consentDocuments[i];
				}
			}

			if (!consentDocument) {
				validationErrors.push("Invalid Consent Document: " + step.consentDocumentTitle);
			} else {
				var sections = [];
				for (var i = 0; i < consentDocument.sections.length; i++) {
					if (consentDocument.sections[i].sectionType != 'OnlyInDocument') {
						sections.push(consentDocument.sections[i]);
					}
				}

				if (!sections.length) {
					validationErrors.push("No valid consent sections for visual consent");
				}
			}
		}
		return validationErrors;
	};

	var consentReviewStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.consentDocumentTitle || !template.consentDocuments) {
			validationErrors.push("Step must reference a valid consent document");
		} else {
			var consentDocument = _.find(template.consentDocuments, function (doc) { return doc.title == step.consentDocumentTitle });
			if (!consentDocument) {
				validationErrors.push("Invalid Consent Document: " + step.consentDocumentTitle);
			}

			if (step.signatureIdentifier) {
				if (!consentDocument || !consentDocument.signatures) {
					validationErrors.push("Invalid Signature: \"" + step.signatureIdentifier + "\"");
				} else {
					var signature = _.find(consentDocument.signatures, function (sig) { return sig.identifier === step.signatureIdentifier });
					if (!signature) {
						validationErrors.push("Invalid Signature: \"" + step.signatureIdentifier + "\"");
					}
				}
			}
		}

		return validationErrors;
	};

	var consentStepValidator = function (step, template) {
		var validationErrors = [];
		var consentDocuments = {};
		for (var i = 0; i < template.steps.length; i++) {
			var currentStep = template.steps[i];
			if (currentStep.type !== "ConsentStep") continue;

			validateConsentTitle(validationErrors, currentStep, "default", consentDocuments);
			for (var lang in currentStep.localizations) {
				validateConsentTitle(validationErrors, currentStep.localizations[lang], lang, consentDocuments);
			}
		}
		return validationErrors;
	}

	function validateConsentTitle(validationErrors, root, lang, consentDocuments) {
		if (!root.consentDocumentTitle) {
			validationErrors.push("Step must contain Title for all languages");
			return;
		}
		if (!consentDocuments[lang]) consentDocuments[lang] = {};
		if (consentDocuments[lang][root.consentDocumentTitle]) {
			validationErrors.push("More than one Consent Step defined with Title  \"" + root.consentDocumentTitle + "\"");
		}
		consentDocuments[lang][root.consentDocumentTitle] = true;
	}

	var videoCaptureStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.devicePosition) {
			validationErrors.push("Step must contain Camera Location");
		}
		if (!step.duration) {
			validationErrors.push("Step must contain Duration");
		}
		return validationErrors;
	};

	var videoInstructionStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.videoURL) {
			validationErrors.push("Step must contain Video Url");
		}
		if (step.image && step.thumbnailTime) {
			validationErrors.push("Step may contain either Thumbnail Url or Thumbnail Time but not both");
		}
		return validationErrors;
	};

	var webViewStepValidator = function (step, template) {
		var validationErrors = [];
		if (step.templateIdentifier) {
			var stepTemplate = _.find(template.webViewStepTemplates, function (t) { return t.identifier == step.templateIdentifier });
			if (!stepTemplate) {
				validationErrors.push("Invalid template: " + step.templateIdentifier);
			}
		}
		if (step.url) {
			var invalidUrl;
			try {
				var checkUrl = new URL(step.url)
				invalidUrl = !_.contains(["http:", "https:"], checkUrl.protocol);
			} catch {
				invalidUrl = true;
			}
			if (invalidUrl) {
				validationErrors.push("Invalid url: " + step.url);
			}
		}
		if (step?.contentMode == "partialHtml" && !template.useWebSurveyEngine) {
			validationErrors.push("Cannot use \"Partial HTML\" content outside of non-legacy Standard Survey template. Switch to \"HTML\" or \"Specify URL\".");
		}
		return validationErrors;
	};

	var connectDeviceAccountStepValidator = function (step, template) {
		var validationErrors = [];
		if (!step.deviceType || step.deviceType.length === 0) {
			validationErrors.push("Step must contain Device Type");
		}
		return validationErrors;
	}

	typeValidators["InstructionStep"] = [baseStepValidator, instructionStepValidator];
	typeValidators["CompletionStep"] = [baseStepValidator];
	typeValidators["QuestionStep"] = [baseStepValidator, questionStepValidator];
	typeValidators["FormStep"] = [baseStepValidator, formStepValidator];
	typeValidators["VisualConsentStep"] = [baseStepValidator, visualConsentStepValidator];
	typeValidators["ConsentReviewStep"] = [baseStepValidator, consentReviewStepValidator];
	typeValidators["ConsentStep"] = [baseStepValidator, consentStepValidator];
	typeValidators["ConsentFormStep"] = [baseStepValidator, formStepValidator];
	typeValidators["WebViewStep"] = [baseStepValidator, webViewStepValidator];
	typeValidators["ImageCaptureStep"] = [baseStepValidator];
	typeValidators["VideoCaptureStep"] = [baseStepValidator, videoCaptureStepValidator];
	typeValidators["CountdownStep"] = [baseStepValidator];
	typeValidators["VideoInstructionStep"] = [baseStepValidator, videoInstructionStepValidator];
	typeValidators["WaitStep"] = [baseStepValidator];
	typeValidators["YouTubeStep"] = [baseStepValidator];
	typeValidators["EndOfSurveyStep"] = [baseStepValidator, endOfSurveyStepValidator];
	typeValidators["CelebrationStep"] = [baseStepValidator];
	typeValidators["ConnectDeviceAccountStep"] = [baseStepValidator, connectDeviceAccountStepValidator];
	typeValidators["ConnectEhrStep"] = [baseStepValidator];

	var formItemValidator = function (formItem, template, customFields) {
		var validationErrors = [];
		if (formItem.answerFormat) {
			validationErrors = validationErrors.concat(prefix(validate(formItem.answerFormat, template, customFields), "Answer Format"));

			if (formItem.hidePredicate) {
				if (!formItem.hidePredicate.type) {
					validationErrors.push("Hide rule for form item is missing condition");
				} else {
					validationErrors = validationErrors.concat(validate(formItem.hidePredicate, template, customFields));
				}
			}
		}
		return validationErrors;
	};
	typeValidators["FormItem"] = [formItemValidator];

	var textAnswerFormatValidator = function (answerFormat, template, customFields) {
		var validationErrors = [];
		if (!answerFormat.validationPattern) {
			if (answerFormat.demographicField && 'MobilePhone' == answerFormat.demographicField) {
				validationErrors.push("Text Answer requires a validation pattern when setting Mobile Phone.");
			}
			else if (answerFormat.demographicField && 'Email' == answerFormat.demographicField) {
				validationErrors.push("Text Answer requires a validation pattern when setting Email.");
			}
			else if (answerFormat.demographicField && 'PostalCode' == answerFormat.demographicField) {
				validationErrors.push("Text Answer requires a validation pattern when setting Postal Code.");
			}
			if (answerFormat.customField) {
				var selectedCF = customFields?.find(function (cf) { return cf.Name == answerFormat.customField });
				if (selectedCF && 'Phone' == selectedCF.DataType) {
					validationErrors.push("Text Answer requires a validation pattern when setting a Phone Custom Field.");
				}
				else if (selectedCF && 'Email' == selectedCF.DataType) {
					validationErrors.push("Text Answer requires a validation pattern when setting an Email Custom Field.");
				}
			}
		}

		if (answerFormat.validationPattern && !answerFormat.invalidMessage) {
			validationErrors.push("Text Answer requires a invalid message when using pattern validation.")
		}

		if (answerFormat.demographicField && answerFormat.customField) {
			var selectedCF = customFields?.find(function (cf) { return cf.Name == answerFormat.customField });
			if (selectedCF) {
				if ('MobilePhone' == answerFormat.demographicField && 'Email' == selectedCF.DataType) {
					validationErrors.push("Text Answer cannot populate a Demographic Field and a Custom Field with different types.");
				}
				else if ('Email' == answerFormat.demographicField && 'Phone' == selectedCF.DataType) {
					validationErrors.push("Text Answer cannot populate a Demographic Field and a Custom Field with different types.");
				}
				else if ('PostalCode' == answerFormat.demographicField && ('Phone' == selectedCF.DataType || 'Email' == selectedCF.DataType)) {
					validationErrors.push("Text Answer cannot populate a Demographic Field and a Custom Field with different types.");
				}
			}
		}

		return validationErrors;
	}
	typeValidators["TextAnswerFormat"] = [textAnswerFormatValidator];

	var textChoiceAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];
		if (!answerFormat.style) {
			validationErrors.push("Text Choice Answer Format must have a style");
		}
		if (!answerFormat.textChoices || !answerFormat.textChoices.length) {
			validationErrors.push("Text Choice Answer Format must have text choices");
		} else {
			for (var i = 0; i < answerFormat.textChoices.length; i++) {
				var textChoice = answerFormat.textChoices[i];
				validationErrors = validationErrors.concat(prefix(validate(textChoice, template), "Text Choice " + i));
			}
		}
		return validationErrors;
	};
	typeValidators["TextChoiceAnswerFormat"] = [textChoiceAnswerFormatValidator];

	var textChoiceValidator = function (textChoice, template) {
		var validationErrors = [];
		if (!textChoice.value) {
			validationErrors.push("Text Choice must have a value");
		}
		if (!textChoice.text) {
			validationErrors.push("Text Choice must have text");
		}
		return validationErrors;
	};
	typeValidators["TextChoice"] = [textChoiceValidator];

	var valuePickerAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];
		if (!answerFormat.textChoices || !answerFormat.textChoices.length) {
			validationErrors.push("Value Picker Answer Format must have text choices");
		} else {
			for (var i = 0; i < answerFormat.textChoices.length; i++) {
				validationErrors = validationErrors.concat(prefix(validate(answerFormat.textChoices[i], template), "Text Choice " + i));
			}
		}
		return validationErrors;
	};
	typeValidators["ValuePickerAnswerFormat"] = [valuePickerAnswerFormatValidator];

	var numericAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];
		if (!answerFormat.style) {
			validationErrors.push("Numeric Answer Format must have a style");
		}
		return validationErrors;
	};
	typeValidators["NumericAnswerFormat"] = [numericAnswerFormatValidator];

	function isValidNumber(number) {
		if (number != undefined && !isNaN(number)) {
			return true;
		}
		return false;
	}

	var phoneAnswerFormatValidator = function (answerFormat, template) {
		return [];
	};
	typeValidators["PhoneAnswerFormat"] = [phoneAnswerFormatValidator];

	var emailAnswerFormatValidator = function (answerFormat, template) {
		if (!answerFormat.invalidMessage) {
			return ["Email Answer Format requires an invalid message"];
		}
		return [];
	};
	typeValidators["EmailAnswerFormat"] = [emailAnswerFormatValidator];

	typeValidators["DateOfBirthAnswerFormat"] = [];

	var postalCodeAnswerFormatValidator = function (answerFormat, template) {
		if (!answerFormat.invalidMessage) {
			return ["Postal Code Answer Format requires an invalid message"];
		}
		return [];
	};
	typeValidators["PostalCodeAnswerFormat"] = [postalCodeAnswerFormatValidator];


	var baseScaleAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];

		var minimumValue = parseFloat(answerFormat.minimumValue);
		var maximumValue = parseFloat(answerFormat.maximumValue);
		if (isValidNumber(minimumValue) && isValidNumber(maximumValue)) {
			if (maximumValue < minimumValue) {
				validationErrors.push("Maximum value cannot be less than minimum value");
			}
		} else {
			if (!isValidNumber(minimumValue)) {
				validationErrors.push("Minimum value is required to be a valid number");
			}

			if (!isValidNumber(maximumValue)) {
				validationErrors.push("Maximum value is required to be a valid number");
			}
		}
		return validationErrors;
	};

	var discreteScaleAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];

		var minimumValue = parseInt(answerFormat.minimumValue);
		var maximumValue = parseInt(answerFormat.maximumValue);
		var step = parseInt(answerFormat.step);
		if (isValidNumber(minimumValue) && isValidNumber(maximumValue) && isValidNumber(step)) {
			if (step < 1) {
				validationErrors.push("Step cannot be less than 1");
			}

			if ((maximumValue - minimumValue) % step != 0) {
				validationErrors.push("The difference between maximumValue and minimumValue should be divisible by step");
			}

			var steps = (maximumValue - minimumValue) / step;
			if (steps > 13 || steps < 1) {
				validationErrors.push("The total number of steps between minimumValue and maximumValue should be more than 1 and no more than 13")
			}

			if (minimumValue < -10000) {
				validationErrors.push("Minimum value cannot be less than -10000")
			}

			if (maximumValue > 10000) {
				validationErrors.push("Maximum value cannot be more than 10000")
			}
		} else if (!isValidNumber(step)) {
			validationErrors.push("Step is required to be a valid number");
		}
		return validationErrors;
	};

	var continuousScaleAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];

		var minimumValue = parseFloat(answerFormat.minimumValue);
		var maximumValue = parseFloat(answerFormat.maximumValue);
		var maximumFractionDigits = parseInt(answerFormat.maximumFractionDigits);
		if (maximumFractionDigits < 0) { maximumFractionDigits = 0; }
		if (maximumFractionDigits > 4) { maximumFractionDigits = 4; }

		if (isValidNumber(minimumValue) && isValidNumber(maximumValue) && isValidNumber(maximumFractionDigits)) {
			var lowerBound = -10000 * Math.pow(0.1, maximumFractionDigits);
			if (minimumValue < lowerBound) {
				validationErrors.push("Minimum value cannot be less than " + lowerBound + " with " + maximumFractionDigits + " fraction digits");
			}

			var upperBound = 10000 * Math.pow(0.1, maximumFractionDigits);
			if (maximumValue > upperBound) {
				validationErrors.push("Maximum value cannot be more than " + upperBound + " with " + maximumFractionDigits + " fraction digits");
			}
		} else if (!isValidNumber(maximumFractionDigits)) {
			validationErrors.push("Maximum fraction digits is required to be a valid number");
		}
		return validationErrors;
	};
	typeValidators["ScaleAnswerFormat"] = [baseScaleAnswerFormatValidator, discreteScaleAnswerFormatValidator];
	typeValidators["ContinuousScaleAnswerFormat"] = [baseScaleAnswerFormatValidator, continuousScaleAnswerFormatValidator];

	var dateAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];
		if (answerFormat.style != "Date" && answerFormat.style != "DateAndTime") {
			validationErrors.push("Invalid style - must be 'Date' or 'DateAndTime'");
		}

		if (answerFormat.defaultDate && !(dateIsValid(answerFormat.defaultDate) || relativeDateIsValid(answerFormat.defaultDate))) {
			validationErrors.push("Invalid default date");
		}
		if (answerFormat.minimumDate && !(dateIsValid(answerFormat.minimumDate) || relativeDateIsValid(answerFormat.minimumDate))) {
			validationErrors.push("Invalid minimum date");
		}
		if (answerFormat.maximumDate && !(dateIsValid(answerFormat.maximumDate) || relativeDateIsValid(answerFormat.maximumDate))) {
			validationErrors.push("Invalid maximum date");
		}

		let calculatedDefaultDate = dateFromDateObjectOrString(answerFormat.defaultDate);
		let calculatedMinimumDate = dateFromDateObjectOrString(answerFormat.minimumDate);
		let calculatedMaximumDate = dateFromDateObjectOrString(answerFormat.maximumDate);

		if (calculatedMinimumDate && calculatedMaximumDate && moment(calculatedMinimumDate).isAfter(moment(calculatedMaximumDate))) {
			validationErrors.push("Minimum date cannot be after maximum date");
		}

		if (calculatedDefaultDate && calculatedMaximumDate && moment(calculatedDefaultDate).isAfter(moment(calculatedMaximumDate))) {
			validationErrors.push("Default date cannot be after maximum date");
		}

		if (answerFormat.defaultDate && answerFormat.defaultValue) {
			validationErrors.push("Use either Default Value or Default Date, not both");
		}

		return validationErrors;
	};
	typeValidators["DateAnswerFormat"] = [dateAnswerFormatValidator];

	var timeOfDayAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];

		if (answerFormat.defaultHour < 0 || answerFormat.defaultHour > 23) {
			validationErrors.push("Hour must be 0-23");
		}
		if (answerFormat.defaultMinute < 0 || answerFormat.defaultMinute > 59) {
			validationErrors.push("Minute must be 0-59");
		}
		if (answerFormat.defaultValue && (answerFormat.defaultHour || answerFormat.defaultMinute)) {
			validationErrors.push("Use either Default Value or Default Hour/Minute, not both");
		}

		return validationErrors;
	};
	typeValidators["TimeOfDayAnswerFormat"] = [timeOfDayAnswerFormatValidator];

	var heightWeightAnswerFormatValidator = function (answerFormat, template) {
		var validationErrors = [];

		var measurementSystems = ["USC", "Metric"];

		if (!_.contains(measurementSystems, answerFormat.measurementSystem)) {
			validationErrors.push("Measurement System must be one of the following: " + measurementSystems.join(", "));
		}

		return validationErrors;
	};
	typeValidators["WeightAnswerFormat"] = [heightWeightAnswerFormatValidator];
	typeValidators["HeightAnswerFormat"] = [heightWeightAnswerFormatValidator];

	const signatureAnswerFormatValidator = function (answerFormat, template) {
		const validationErrors = [];
		if (!template.useWebSurveyEngine) {
			validationErrors.push("Signature answer format is only available in V2 surveys");
		}

		if (!answerFormat.signatureIdentifier) {
			validationErrors.push("Signature identifier required");
		}

		if (!answerFormat.requiresName && !answerFormat.requiresSignatureImage) {
			validationErrors.push("At least one of 'Requires Name' or 'Requires Signature Image' must be selected");
		}

		return validationErrors;
	};
	typeValidators["SignatureAnswerFormat"] = [signatureAnswerFormatValidator];

	typeValidators["BooleanAnswerFormat"] = [];

	typeValidators["FileAnswerFormat"] = [function (answerFormat, template) {
		var validationErrors = [];

		if (!template.useWebSurveyEngine) {
			validationErrors.push("File answer format is only available in V2 surveys");
		}

		if (!answerFormat.accept) {
			validationErrors.push("Answer format must accept at least one type of file");
		}

		if (answerFormat.fileName && !/^[a-zA-Z0-9\-_\.\'\(\) ]+$/.test(answerFormat.fileName)) {
			validationErrors.push("Invalid file name. Only alphanumeric characters, spaces, and the following special characters are allowed: - _ . ' ( )");
		}

		if (answerFormat.fileName && answerFormat.fileName.includes(".")) {
			validationErrors.push("Invalid file name. Do not include file extension in file name.");
		}

		return validationErrors;
	}];

	var validDestinationStepIdentifierValidator = function (navigationItem, template) {
		if (!navigationItem.destinationStepIdentifier) {
			return ["No destination step specified"];
		}

		if (getFinishStep(navigationItem.destinationStepIdentifier)) {
			return [];
		}

		for (var i = 0; i < template.steps.length; i++) {
			if (template.steps[i].identifier == navigationItem.destinationStepIdentifier) {
				return [];
			}
		}
		return ["Invalid destination step: " + navigationItem.destinationStepIdentifier + " does not exist"];
	};
	typeValidators["DirectStepNavigationRule"] = [validDestinationStepIdentifierValidator];

	var predicateStepNavigationRuleValidator = function (navigationRule, template) {
		var validationErrors = [];
		if (!navigationRule.resultPredicateDestinations || !navigationRule.resultPredicateDestinations.length) {
			validationErrors.push("At least one navigation rule is required");
			return validationErrors;
		}

		for (var i = 0; i < navigationRule.resultPredicateDestinations.length; i++) {
			validationErrors = validationErrors.concat(validate(navigationRule.resultPredicateDestinations[i], template));
		}
		return validationErrors;
	};
	typeValidators["PredicateStepNavigationRule"] = [predicateStepNavigationRuleValidator];

	var predicateSkipStepNavigationRuleValidator = function (navigationRule, template) {
		var validationErrors = [];
		if (!navigationRule.resultPredicate || !navigationRule.resultPredicate.type) {
			validationErrors.push("Condition is required");
			return validationErrors;
		}
		validationErrors = validationErrors.concat(validate(navigationRule.resultPredicate, template));
		return validationErrors;
	};

	typeValidators["PredicateSkipStepNavigationRule"] = [predicateSkipStepNavigationRuleValidator];

	var validationRuleValidator = function (validationRule, template) {
		var validationErrors = [];
		if (!validationRule.resultPredicate || !validationRule.resultPredicate.type) {
			validationErrors.push("Validation rule missing condition");
		} else {
			validationErrors = validationErrors.concat(validate(validationRule.resultPredicate, template));
		}

		if (!validationRule.errorMessage) {
			validationErrors.push("Validation rule missing error message");
		}

		return validationErrors;
	};
	typeValidators["ValidationRule"] = [validationRuleValidator];

	var resultPredicateDestinationValidator = function (resultPredicateDestination, template) {
		if (!resultPredicateDestination.resultPredicate.type) {
			return ["Empty navigation rule"];
		}

		return validate(resultPredicateDestination.resultPredicate, template);
	};
	typeValidators["ResultPredicateDestination"] = [validDestinationStepIdentifierValidator, resultPredicateDestinationValidator];

	var notCompoundPredicateValidator = function (notCompoundPredicate, template) {
		if (!notCompoundPredicate.subpredicate) {
			return ["Not predicate must have a subpredicate"];
		}

		if (!notCompoundPredicate.subpredicate.type) {
			return ["Empty navigation rule"];
		}

		return validate(notCompoundPredicate.subpredicate, template);
	};
	typeValidators["NotCompoundPredicate"] = [notCompoundPredicateValidator];

	var subpredicatesValidator = function (compoundPredicate, template) {
		if (!compoundPredicate.subpredicates || !(compoundPredicate.subpredicates.length >= 2)) {
			return ["For a compound condition (AND/OR), at least two nested conditions are required"];
		}

		var validationErrors = [];
		for (var i = 0; i < compoundPredicate.subpredicates.length; i++) {
			if (!compoundPredicate.subpredicates[i].type) {
				validationErrors.push("Empty navigation rule");
			}
			validationErrors = validationErrors.concat(validate(compoundPredicate.subpredicates[i], template));
		}

		return validationErrors;
	};
	typeValidators["AndCompoundPredicate"] = [subpredicatesValidator];
	typeValidators["OrCompoundPredicate"] = [subpredicatesValidator];

	function validateResultIdentifierAnswerFormat(predicate, template, expectedAnswerFormatTypes) {
		function formatResultIdentifier() {
			if (predicate.stepIdentifier) {
				return predicate.stepIdentifier + " > " + predicate.resultIdentifier;
			}
			return predicate.resultIdentifier;
		}

		var resultIdentifier = predicate.resultIdentifier;
		var stepIdentifier = predicate.stepIdentifier;
		if (!stepIdentifier) {
			stepIdentifier = resultIdentifier;
		}

		var answerFormat;
		for (var i = 0; i < template.steps.length; i++) {
			if (template.steps[i].identifier == stepIdentifier) {
				if (template.steps[i].type == 'FormStep' || template.steps[i].type == 'ConsentFormStep') {
					for (var j = 0; j < template.steps[i].formItems.length; j++) {
						if (template.steps[i].formItems[j].identifier == resultIdentifier) {
							answerFormat = template.steps[i].formItems[j].answerFormat;
						}
					}
				}
				else if (template.steps[i].type == 'WebViewStep') {
					answerFormat = { type: 'WebViewAnswerFormat' };
				}
				else {
					answerFormat = template.steps[i].answerFormat;
				}
			}
		}
		if (!answerFormat) {
			return ["Unknown result identifier: " + formatResultIdentifier()];
		}

		if (expectedAnswerFormatTypes && !_.contains(expectedAnswerFormatTypes, answerFormat.type)) {
			return ["Invalid answer format:" + answerFormat.type + " - expected " + expectedAnswerFormatTypes];
		}

		return [];
	}

	var textQuestionResultPredicatePredicateValidator = function (predicate, template) {
		var validationErrors = [];
		validationErrors = validationErrors.concat(validateResultIdentifierAnswerFormat(predicate, template, ['TextAnswerFormat', 'EmailAnswerFormat', 'PhoneAnswerFormat', 'PostalCodeAnswerFormat']))

		if (!predicate.expectedString) {
			validationErrors.push("Expected value is required");
		}
		return validationErrors;
	};

	var choiceQuestionResultPredicateValidator = function (predicate, template) {
		var validationErrors = [];
		validationErrors = validationErrors.concat(validateResultIdentifierAnswerFormat(predicate, template, ['TextChoiceAnswerFormat', 'ValuePickerAnswerFormat']))

		if (!predicate.expectedString &&
			!predicate.expectedStrings) {
			validationErrors.push("Expected value is required");
		}
		return validationErrors;
	};

	var numericQuestionResultPredicatePropertiesValidator = function (predicate, template) {
		if (!predicate.hasOwnProperty("expectedAnswer") &&
			!predicate.hasOwnProperty("minimumExpectedAnswerValue") &&
			!predicate.hasOwnProperty("maximumExpectedAnswerValue")) {
			return ["At least one of 'expectedAnswer', 'minimumExpectedAnswerValue', or 'maximumExpectedAnswerValue' is required"];
		}

		if (predicate.hasOwnProperty("minimumExpectedAnswerValue") &&
			predicate.hasOwnProperty("maximumExpectedAnswerValue") &&
			predicate.maximumExpectedAnswerValue <= predicate.minimumExpectedAnswerValue) {
			return ["Minimum expected value must be less than maximum expected value"];
		}
		return [];
	};

	var numericQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template, ['NumericAnswerFormat']);
	};

	var scaleQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template, ['ScaleAnswerFormat', 'ContinuousScaleAnswerFormat']);
	};

	var booleanQuestionResultPredicateValidator = function (predicate, template) {
		var validationErrors = [];
		validationErrors = validationErrors.concat(validateResultIdentifierAnswerFormat(predicate, template, ['BooleanAnswerFormat']))

		if (!predicate.hasOwnProperty("expectedAnswer")) {
			validationErrors.push("Expected answer is required");
		}
		return validationErrors;
	};

	var consentPredicateValidator = function (predicate, template) {
		var validationErrors = [];

		for (var i = 0; i < template.steps.length; i++) {
			if (template.steps[i].identifier == predicate.stepIdentifier) {
				if (template.steps[i].type == 'ConsentReviewStep') {
					if (template.steps[i].signatureIdentifier && template.steps[i].signatureIdentifier != predicate.resultIdentifier) {
						validationErrors.push("Invalid signature identifier: " + predicate.resultIdentifier);
					}
				} else if (template.steps[i].type == 'ConsentStep') {
					if (!template.steps[i].requiresName && !template.steps[i].requiresSignatureImage) {
						validationErrors.push("Signature is required for consent predicate");
					}
					if ("Participant" != predicate.resultIdentifier) {
						validationErrors.push("Invalid signature identifier: " + predicate.resultIdentifier);
					}
				} else {
					validationErrors.push("Invalid step type for consent predicate");
				}
			}
		}

		if (!predicate.hasOwnProperty("didConsent")) {
			validationErrors.push("Did consent is required");
		}
		return validationErrors;
	};

	var webViewResultPredicateValidator = function (predicate, template) {
		var validationErrors = [];
		validationErrors = validationErrors.concat(validateResultIdentifierAnswerFormat(predicate, template, ['WebViewAnswerFormat']))

		if (_.isUndefined(predicate.expectedString) === _.isUndefined(predicate.matchingPattern)) {
			validationErrors = validationErrors.concat(["Please specify one of either an expected string, or a Regular Expression pattern to match"]);
		}

		return validationErrors;
	};

	var dateQuestionResultPredicatePropertiesValidator = function (predicate, template) {
		if (_.isUndefined(predicate.minimumExpectedDate) &&
			_.isUndefined(predicate.maximumExpectedDate)) {
			return ["Please specify the minimum or maximum expected date for a date-based condition"];
		}

		if (!_.isUndefined(predicate.minimumExpectedDate) &&
			!_.isUndefined(predicate.maximumExpectedDate)) {
			var minimumDate = dateTimeFromDateObjectOrString(predicate.minimumExpectedDate);
			var maximumDate = dateTimeFromDateObjectOrString(predicate.maximumExpectedDate);
			if (minimumDate && maximumDate && isAfter(minimumDate, maximumDate)) {
				return ["Minimum date must be less than maximum date"];
			}
		}
		return [];
	};

	var dateQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template, ['DateAnswerFormat', 'DateOfBirthAnswerFormat']);
	};

	var timeOfDayQuestionResultPredicatePropertiesValidator = function (predicate, template) {
		if (_.isUndefined(predicate.minimumExpectedDate) &&
			_.isUndefined(predicate.maximumExpectedDate)) {
			return ["Please specify the minimum or maximum expected time of day for a time-based condition"];
		}

		if (!_.isUndefined(predicate.minimumExpectedDate) &&
			!_.isUndefined(predicate.maximumExpectedDate) &&
			dateIsValid(predicate.minimumExpectedDate) &&
			dateIsValid(predicate.maximumExpectedDate) &&
			moment(predicate.minimumExpectedDate).isAfter(moment(predicate.maximumExpectedDate))) {
			return ["Minimum time of day must be less than maximum time of day"];
		}
		return [];
	};

	var timeOfDayQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template, ['TimeOfDayAnswerFormat']);
	};

	var heightWeightQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template, ['HeightAnswerFormat', 'WeightAnswerFormat']);
	};

	var validationDemographicAgePredicateValidator = function (predicate, template) {
		switch (predicate.operator) {
			case 'IsEqual':
				if (predicate.expectedValue == null) {
					return ["Expected value is required"];
				}
				break;
			case 'Minimum':
				if (predicate.minimumExpectedValue == null) {
					return ["Minimum expected value is required"];
				}
				break;
			case 'Maximum':
				if (predicate.maximumExpectedValue == null) {
					return ["Maximum expected value is required"];
				}
				break;
			case 'Between':
				if (predicate.minimumExpectedValue == null) {
					return ["Minimum expected value is required"];
				}
				if (predicate.maximumExpectedValue == null) {
					return ["Maximum expected values are required"];
				}
				if (predicate.maximumExpectedValue <= predicate.minimumExpectedValue) {
					return ["Minimum expected value must be less than maximum expected value"];
				}
				break;
			default:
				return ["Invalid operator for Age demographic predicate"];
		}
		return [];
	};

	var demographicFieldPredicateValidator = function (predicate, template) {
		if (predicate.demographicField === 'Age') {
			return validationDemographicAgePredicateValidator(predicate, template);
		}
		return [];
	}

	var nilQuestionResultPredicateValidator = function (predicate, template) {
		return validateResultIdentifierAnswerFormat(predicate, template);
	};

	var institutionPredicateValidator = function (predicate, template) {
		if (predicate.operator === 'IsEqual' && !predicate.expectedValue) {
			return ["Expected value is required"];
		}

		return [];
	}

	var expressionPredicateValidator = function (predicate, template) {
		const validationErrors = [];
		if (!predicate.expression) {
			validationErrors.push("Expression value is required");
		}

		return validationErrors;
	}

	var participantsInSegmentPredicateValidator = function (predicate, template) {
		const validationErrors = [];
		if (!predicate.segment) {
			validationErrors.push("Segment is required");
		}

		const hasMinimumExpectedValue = predicate.minimumExpectedValue !== undefined && predicate.minimumExpectedValue !== null && predicate.minimumExpectedValue !== '';
		const hasMaximumExpectedValue = predicate.maximumExpectedValue !== undefined && predicate.maximumExpectedValue !== null && predicate.maximumExpectedValue !== '';
		if (!hasMinimumExpectedValue && !hasMaximumExpectedValue) {
			validationErrors.push("At least one of minimum or maximum expected value is required");
		}
		if (hasMinimumExpectedValue) {
			if (!Number.isInteger(predicate.minimumExpectedValue) || predicate.minimumExpectedValue < 0) {
				validationErrors.push("Minimum expected value must be a non-negative integer");
			}
		}
		if (hasMaximumExpectedValue) {
			if (!Number.isInteger(predicate.maximumExpectedValue) || predicate.maximumExpectedValue < 0) {
				validationErrors.push("Maximum expected value must be a non-negative integer");
			}
		}
		if (
			hasMinimumExpectedValue &&
			hasMaximumExpectedValue &&
			Number.isInteger(predicate.minimumExpectedValue) &&
			Number.isInteger(predicate.maximumExpectedValue) &&
			predicate.maximumExpectedValue < predicate.minimumExpectedValue
		) {
			validationErrors.push("Maximum expected value must be greater than or equal to minimum expected value");
		}
		return validationErrors;
	}

	var commonExpressionPredicateValidator = function (predicate, template) {
		var validationResult = validateExpression(predicate.expression?.expression, "Boolean");
		if (validationResult) {
			return [validationResult];
		}
		return [];
	}

	typeValidators["BooleanQuestionResultPredicate"] = [booleanQuestionResultPredicateValidator];
	typeValidators["ConsentPredicate"] = [consentPredicateValidator];
	typeValidators["WebViewResultPredicate"] = [webViewResultPredicateValidator];
	typeValidators["TextQuestionResultPredicate"] = [textQuestionResultPredicatePredicateValidator];
	typeValidators["ChoiceQuestionResultPredicate"] = [choiceQuestionResultPredicateValidator];
	typeValidators["NumericQuestionResultPredicate"] = [numericQuestionResultPredicateValidator, numericQuestionResultPredicatePropertiesValidator];
	typeValidators["ScaleQuestionResultPredicate"] = [scaleQuestionResultPredicateValidator, numericQuestionResultPredicatePropertiesValidator];
	typeValidators["DateQuestionResultPredicate"] = [dateQuestionResultPredicateValidator, dateQuestionResultPredicatePropertiesValidator];
	typeValidators["TimeOfDayQuestionResultPredicate"] = [timeOfDayQuestionResultPredicateValidator, timeOfDayQuestionResultPredicatePropertiesValidator];
	typeValidators["HeightWeightQuestionResultPredicate"] = [heightWeightQuestionResultPredicateValidator];
	typeValidators["CustomFieldPredicate"] = [];
	typeValidators["DemographicFieldPredicate"] = [demographicFieldPredicateValidator];
	typeValidators["InstitutionPredicate"] = [institutionPredicateValidator];
	typeValidators["ExpressionPredicate"] = [expressionPredicateValidator];
	typeValidators["CommonExpressionPredicate"] = [commonExpressionPredicateValidator];
	typeValidators["NilQuestionResultPredicate"] = [nilQuestionResultPredicateValidator];
	typeValidators["ParticipantsInSegmentPredicate"] = [participantsInSegmentPredicateValidator];

	function isValidInteger(value) {
		return !isNaN(value) &&
			parseInt(Number(value)) == value &&
			!isNaN(parseInt(value, 10));
	}

	function isValidFloat(value) {
		return !isNaN(value) &&
			parseFloat(Number(value)) == value &&
			!isNaN(parseFloat(value));
	}

	var predefinedAudioTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (_.isUndefined(template.checkAudioLevel)) { validationErrors.push("Check Audio Level is required"); }
		if (!isValidFloat(template.duration)) { validationErrors.push("Duration is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.duration < 5) {
			validationErrors.push("Duration cannot be shorter than 5 seconds");
		}
		return validationErrors;
	};

	var predefinedFitnessTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.walkDuration)) { validationErrors.push("Walk Duration is required"); }
		if (!isValidFloat(template.restDuration)) { validationErrors.push("Rest Duration is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.walkDuration < 5) {
			validationErrors.push("Walk Duration cannot be shorter than 5 seconds");
		}

		if (template.restDuration < 5) {
			validationErrors.push("Rest Duration cannot be shorter than 5 seconds");
		}
		return validationErrors;
	};

	var predefinedHolePegTestTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (_.isUndefined(template.dominantHand)) { validationErrors.push("Dominant Hand is required"); }
		if (!isValidInteger(template.numberOfPegs)) { validationErrors.push("Number of Pegs is required"); }
		if (!isValidFloat(template.threshold)) { validationErrors.push("Threshold is required"); }
		if (_.isUndefined(template.rotated)) { validationErrors.push("Rotated is required"); }
		if (!isValidFloat(template.timeLimit)) { validationErrors.push("Time Limit is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.dominantHand != 'Left' && template.dominantHand != 'Right') {
			validationErrors.push("Dominant Hand must be Left or Right");
		}

		if (template.numberOfPegs < 1) {
			validationErrors.push("Number of Pegs cannot be less than 1");
		}

		if (template.threshold < 0 || template.threshold > 1) {
			validationErrors.push("Threshold must be greater than or equal to 0 and less than or equal to 1");
		}

		if (template.timeLimit < 1) {
			validationErrors.push("Time limit must be greater than 1");
		}
		return validationErrors;
	};

	var predefinedPSATTaskValidator = function (predicate, template) {
		var validationErrors = [];

		if (_.isUndefined(template.presentationMode) || !(template.presentationMode.constructor === Array)) { validationErrors.push("Presentation Mode is required"); }
		if (!isValidFloat(template.interStimulusInterval)) { validationErrors.push("Inter-Stimulus Interval is required"); }
		if (!isValidFloat(template.stimulusDuration)) { validationErrors.push("Stimulus Duration is required"); }
		if (!isValidInteger(template.seriesLength)) { validationErrors.push("Series Length is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (!template.presentationMode.length) {
			validationErrors.push("Presentation Mode must be auditory and/or visual");
		}

		if (template.interStimulusInterval < 1 || template.interStimulusInterval > 5) {
			validationErrors.push("Inter-Stimulus Interval must be greater than or equal to 1 second and less than or equal to 5 seconds");
		}

		if (template.presentationMode.indexOf("Visual") != -1 && (template.stimulusDuration < 0.2 || template.stimulusDuration > template.interStimulusInterval)) {
			validationErrors.push("Stimulus Duration must be greater than or equal to 0.2 seconds and less than the Inter-Stimulus Interval");
		}

		if (template.seriesLength < 10 || template.seriesLength > 120) {
			validationErrors.push("Series Length must be greater than or equal to 10 and less than or equal to 120");
		}
		return validationErrors;
	};

	var predefinedRangeOfMotionTaskValidator = function (predicate, template) {
		if (_.isUndefined(template.limbOption) || !(template.limbOption.constructor === Array)) { return ["Limb Option is required"]; }
		return [];
	};

	var predefinedReactionTimeTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.minimumStimulusInterval)) { validationErrors.push("Minimum Stimulus Interval is required"); }
		if (!isValidFloat(template.maximumStimulusInterval)) { validationErrors.push("Maximum Stimulus Interval is required"); }
		if (!isValidFloat(template.thresholdAcceleration)) { validationErrors.push("Threshold Acceleration is required"); }
		if (!isValidFloat(template.timeout)) { validationErrors.push("Timeout is required"); }
		if (!isValidInteger(template.numberOfAttempts)) { validationErrors.push("Number of Attempts is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.minimumStimulusInterval <= 0) {
			validationErrors.push("Minimum Stimulus Interval must be greater than 0");
		}

		if (template.maximumStimulusInterval <= template.minimumStimulusInterval) {
			validationErrors.push("Maximum Stimulus Interval must be greater than Minimum Stimulus Interval");
		}

		if (template.thresholdAcceleration <= 0) {
			validationErrors.push("Threshold Acceleration must be greater than 0");
		}

		if (template.timeout <= 0) {
			validationErrors.push("Timeout must be greater than 0");
		}

		if (template.numberOfAttempts <= 0) {
			validationErrors.push("Number of Attempts must be greater than 0");
		}
		return validationErrors;
	};

	var predefinedShortWalkTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidInteger(template.numberOfStepsPerLeg)) { validationErrors.push("Number of Steps Per Leg is required"); }
		if (!isValidFloat(template.restDuration)) { validationErrors.push("Rest Duration is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.numberOfStepsPerLeg < 1) {
			validationErrors.push("Number of Steps Per Leg cannot be less than 1");
		}

		if (template.restDuration < 5) {
			validationErrors.push("Rest Duration cannot be shorter than 5 seconds");
		}
		return validationErrors;
	};

	var predefinedSpatialSpanMemoryTaskValidator = function (predicate, template) {
		var validationErrors = [];

		if (!isValidInteger(template.initialSpan)) { validationErrors.push("Initial Span is required"); }
		if (!isValidInteger(template.minimumSpan)) { validationErrors.push("Minimum Span is required"); }
		if (!isValidInteger(template.maximumSpan)) { validationErrors.push("Maximum Span is required"); }
		if (!isValidFloat(template.playSpeed)) { validationErrors.push("Play Speed is required"); }
		if (!isValidInteger(template.maximumTests)) { validationErrors.push("Maximum Tests is required"); }
		if (!isValidInteger(template.maximumConsecutiveFailures)) { validationErrors.push("Maximum Consecutive Failures is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.initialSpan < 2) {
			validationErrors.push("Initial span cannot be less than 2");
		}
		if (template.minimumSpan > template.initialSpan) {
			validationErrors.push("Initial span cannot be less than minimum span");
		}
		if (template.initialSpan > template.maximumSpan) {
			validationErrors.push("Maximum span cannot be less than initial span");
		}
		if (template.maximumSpan > 20) {
			validationErrors.push("Maximum span cannot be more than 20");
		}
		if (template.playSpeed < 0.5) {
			validationErrors.push("Play Speed cannot be shorter than 0.5 Seconds");
		}
		if (template.playSpeed > 20) {
			validationErrors.push("Play Speed cannot be longer than 20 Seconds");
		}
		if (template.maximumTests < 1) {
			validationErrors.push("Maximum Tests cannot be less than 1");
		}
		if (template.maximumTests < 1) {
			validationErrors.push("Maximum Consecutive Failures cannot be less than 1");
		}
		return validationErrors;
	};

	var predefinedStroopTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidInteger(template.numberOfAttempts)) { validationErrors.push("Number of Attempts is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.numberOfAttempts < 10) {
			validationErrors.push("Number of attempts must be greater than or equal to 10");
		}
		return validationErrors;
	};

	var predefinedTimedWalkTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.distanceInMeters)) { validationErrors.push("Distance is required"); }
		if (!isValidFloat(template.timeLimit)) { validationErrors.push("Time Limit is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.distanceInMeters < 1 || template.distanceInMeters > 10000) {
			validationErrors.push("Distance must be greater than or equal to 1 meter or less than or equal to 10000 meters");
		}

		if (template.timeLimit != 0 && template.timeLimit < 1) {
			validationErrors.push("Time Limit must either be 0 (no limit) or greater than 1 second");
		}
		return validationErrors;
	};

	var predefinedToneAudiometryTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.toneDuration)) { validationErrors.push("Tone Duration is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.toneDuration < 5) {
			validationErrors.push("Tone duration cannot be shorter than 5 seconds");
		}
		return validationErrors;
	};

	var predefinedTowerOfHanoiTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidInteger(template.numberOfDisks)) { return ["Number of disks is required"]; }

		if (template.numberOfDisks < 1 || template.numberOfDisks > 8) {
			validationErrors.push("Number of disks must be greater than or equal to 1 and less than or equal to 8");
		}
		return validationErrors;
	};

	var predefinedTrailmakingTaskValidator = function (predicate, template) {
		if (_.isUndefined(template.trailType)) { return ["Trail Type is required"]; }

		var validationErrors = [];
		if (template.trailType != 'A' && template.trailType != 'B') {
			validationErrors.push("Trail Type must be A or B");
		}
		return validationErrors;
	};

	var predefinedTremorTestTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.activeStepDuration)) { validationErrors.push("Active Step Duration is required"); }
		if (_.isUndefined(template.handOptions) || !(template.handOptions.constructor === Array)) { validationErrors.push("Hand Options is required"); }
		if (_.isUndefined(template.activeTaskOptions) || !(template.activeTaskOptions.constructor === Array)) { validationErrors.push("Active Task Options is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.activeStepDuration <= 0) {
			validationErrors.push("Step duration must be greater than 0");
		}
		return validationErrors;
	};

	var predefinedTwoFingerTappingIntervalTaskValidator = function (predicate, template) {
		var validationErrors = [];
		if (!isValidFloat(template.duration)) { validationErrors.push("Duration is required"); }
		if (_.isUndefined(template.handOptions) || !(template.handOptions.constructor === Array)) { validationErrors.push("Hand Options is required"); }
		if (validationErrors.length) {
			return validationErrors;
		}

		if (template.duration < 5) {
			validationErrors.push("Step duration cannot be shorter than 5 seconds");
		}
		return validationErrors;
	};

	typeValidators["PredefinedAudioTask"] = [predefinedAudioTaskValidator];
	typeValidators["PredefinedFitnessTask"] = [predefinedFitnessTaskValidator];
	typeValidators["PredefinedHolePegTestTask"] = [predefinedHolePegTestTaskValidator];
	typeValidators["PredefinedPSATTask"] = [predefinedPSATTaskValidator];
	typeValidators["PredefinedKneeRangeOfMotionTask"] = [predefinedRangeOfMotionTaskValidator];
	typeValidators["PredefinedShoulderRangeOfMotionTask"] = [predefinedRangeOfMotionTaskValidator];
	typeValidators["PredefinedReactionTimeTask"] = [predefinedReactionTimeTaskValidator];
	typeValidators["PredefinedShortWalkTask"] = [predefinedShortWalkTaskValidator];
	typeValidators["PredefinedSpatialSpanMemoryTask"] = [predefinedSpatialSpanMemoryTaskValidator];
	typeValidators["PredefinedStroopTask"] = [predefinedStroopTaskValidator];
	typeValidators["PredefinedTimedWalkTask"] = [predefinedTimedWalkTaskValidator];
	typeValidators["PredefinedToneAudiometryTask"] = [predefinedToneAudiometryTaskValidator];
	typeValidators["PredefinedTowerOfHanoiTask"] = [predefinedTowerOfHanoiTaskValidator];
	typeValidators["PredefinedTrailmakingTask"] = [predefinedTrailmakingTaskValidator];
	typeValidators["PredefinedTremorTestTask"] = [predefinedTremorTestTaskValidator];
	typeValidators["PredefinedTwoFingerTappingIntervalTask"] = [predefinedTwoFingerTappingIntervalTaskValidator];
	typeValidators["PredefinedWalkBackAndForthTask"] = [predefinedFitnessTaskValidator];

	typeValidators["WebViewStepTemplate"] = [function (webViewStepTemplate, template) {
		var validationErrors = [];
		if (!webViewStepTemplate.identifier) { validationErrors.push("Template must have an identifier"); }
		var sameIdentifier = _.find(template.webViewStepTemplates, function (t) {
			return t.identifier == webViewStepTemplate.identifier && t != webViewStepTemplate;
		});
		if (sameIdentifier) { validationErrors.push("Template identifier must be unique"); }
		for (var i = 0; i < webViewStepTemplate.fields.length; i++) {
			validationErrors = validationErrors.concat(prefix(validate(webViewStepTemplate.fields[i], template), webViewStepTemplate.fields[i].identifier));
		}
		return validationErrors;
	}];

	typeValidators["WebViewStepTemplateField"] = [function (field, template) {
		var validationErrors = [];
		if (field.identifier == 'localizations') { validationErrors.push("Field cannot be named 'localizations'"); }
		if (!field.identifier) { validationErrors.push("Field must have an identifier"); }
		if (field.identifier && field.identifier.includes(".")) { validationErrors.push("Field identifier cannot contain '.'"); }
		if (!field.editorType) { validationErrors.push("Field must have an editor type"); }
		return validationErrors;
	}];

	typeValidators["CalculatedField"] = [validateCalculatedField];

	return {
		validate: validate
	};
};

angular.module('ResearchKitApplicationModule').factory("templateValidator", [templateValidatorFactory]);